import { BoxProps, Column, Box } from "@hightouch/htui";
import { usePulseStaggerAnimation } from "./utils";

/**
 * Loader animation for heat map charts.
 * Staggered pulse effect in a diagonal ripple pattern.
 */
export const HeatMapChartLoader = (props: BoxProps) => {
  const { className, scope } = usePulseStaggerAnimation({
    staggerDelay: 0.125,
  });

  return (
    <Column align="start" ref={scope} boxSize="100%" flex={1} {...props}>
      <HeatmapSvg className={className} />
    </Column>
  );
};

function HeatmapSvg({ className }: { className: string }) {
  return (
    <Box
      as="svg"
      width="100%"
      maxW="100%"
      height="auto"
      viewBox="16.7 18.6 416.5 213.3"
      fill="gray.200"
    >
      <defs>
        <mask id="fadeMask">
          <rect width="100%" height="100%" fill="url(#fadeGradient)" />
        </mask>
        <linearGradient id="fadeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>

      <g mask="url(#fadeMask)">
        {/* Diagonal 1: Top-left corner only */}
        <g className={className}>
          <rect
            x="16.7002"
            y="62.6279"
            width="61.5839"
            height="8.35036"
            rx="3.13139"
          />
        </g>

        {/* Diagonal 2: Second diagonal line */}
        <g className={className}>
          <rect
            x="87.6787"
            y="18.5928"
            width="46.2749"
            height="8.35036"
            rx="3.13139"
          />
          <rect
            x="16.7002"
            y="103.857"
            width="61.5839"
            height="8.35036"
            rx="3.13139"
          />
        </g>

        {/* Diagonal 3: Third diagonal line */}
        <g className={className}>
          <rect
            x="143.348"
            y="18.5928"
            width="46.2749"
            height="8.35036"
            rx="3.13139"
          />
          <rect
            x="83.5039"
            y="46.5791"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="16.7002"
            y="145.088"
            width="61.5839"
            height="8.35036"
            rx="3.13139"
          />
        </g>

        {/* Diagonal 4: Fourth diagonal line */}
        <g className={className}>
          <rect
            x="199.017"
            y="18.5928"
            width="46.2749"
            height="8.35036"
            rx="3.13139"
          />
          <rect
            x="139.173"
            y="46.5791"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="83.5039"
            y="93.1592"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="16.7002"
            y="186.317"
            width="61.5839"
            height="8.35036"
            rx="3.13139"
          />
        </g>

        {/* Diagonal 5: Fifth diagonal line */}
        <g className={className}>
          <rect
            x="254.686"
            y="18.5928"
            width="46.2749"
            height="8.35036"
            rx="3.13139"
          />
          <rect
            x="194.842"
            y="46.5791"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="139.173"
            y="93.1592"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="83.5039"
            y="139.738"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
        </g>

        {/* Diagonal 6: Sixth diagonal line */}
        <g className={className}>
          <rect
            x="310.355"
            y="18.5928"
            width="46.2749"
            height="8.35036"
            rx="3.13139"
          />
          <rect
            x="250.511"
            y="46.5791"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="194.842"
            y="93.1592"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="139.173"
            y="139.738"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="83.5039"
            y="186.317"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
        </g>

        {/* Diagonal 7: Seventh diagonal line */}
        <g className={className}>
          <rect
            x="366.023"
            y="18.5928"
            width="46.2749"
            height="8.35036"
            rx="3.13139"
          />
          <rect
            x="306.181"
            y="46.5791"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="250.511"
            y="93.1592"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="194.842"
            y="139.738"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
        </g>

        {/* Diagonal 8: Eighth diagonal line */}
        <g className={className}>
          <rect
            x="361.849"
            y="46.5791"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="306.181"
            y="93.1592"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="250.511"
            y="139.738"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
        </g>

        {/* Diagonal 9: Ninth diagonal line */}
        <g className={className}>
          <rect
            x="361.849"
            y="93.1592"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
          <rect
            x="306.181"
            y="139.738"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
        </g>

        {/* Diagonal 10: Tenth diagonal line */}
        <g className={className}>
          <rect
            x="361.849"
            y="139.738"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
        </g>

        {/* Diagonal 11: Bottom-right corner only */}
        <g className={className}>
          <rect
            x="361.849"
            y="186.317"
            width="54.6253"
            height="45.5356"
            rx="6.26277"
          />
        </g>
      </g>
    </Box>
  );
}
